import { Button, Tooltip } from '@fluentui/react-components';
import { ClipboardPulse20Regular } from '@fluentui/react-icons';
import { saveAs } from 'file-saver';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDoesCalculationDiagnosticsExist, useGetCalculationDiagnosticsHook } from '../../api/report/report';
import { useCurrentEnterprise } from '../../contexts/useCurrentEnterprise';
import { useTaxReturnCalculationState } from '../../hooks/useTaxReturnCalculationState';

interface ICalculationDiagnosticsButtonProps {
    reportId: number;
    returnName?: string;
}

const CalculationDiagnosticsButton: React.FC<ICalculationDiagnosticsButtonProps> = ({ reportId, returnName }) => {
    const { t } = useTranslation();
    const { enterprise } = useCurrentEnterprise();
    const enterpriseKey = enterprise?.enterpriseKey;
    const { registerDependency, isReportExecuting } = useTaxReturnCalculationState();
    const isCalculating = isReportExecuting(reportId);
    const params = { returnKey: reportId, enterpriseKey };
    const { data: doesExist, isFetching, queryKey } = useDoesCalculationDiagnosticsExist(params);
    const getDiagnosticsFn = useGetCalculationDiagnosticsHook();

    useEffect(() => {
        registerDependency(reportId, queryKey);
    }, [reportId, queryKey]);

    const handleDownload = async () => {
        const blob = await getDiagnosticsFn({ returnKey: reportId, enterpriseKey });
        const now = new Date();
        const timestamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(
            2,
            '0'
        )}_${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}-${String(now.getSeconds()).padStart(
            2,
            '0'
        )}`;
        const baseName = returnName ?? String(reportId);
        saveAs(blob, `${baseName}_Diagnostics_${timestamp}.xlsx`);
    };

    // 200 + true/false when CASS exposes the endpoint. When it does not, the BFF returns Ok(null),
    // which ASP.NET sends as 204 with an empty body; UseCustomInstance reads that as "" (not JSON
    // null). undefined means the query has not settled. Hide until we have a definitive boolean.
    if (typeof doesExist !== 'boolean') return null;
    const diagnosticsState = doesExist;

    const label = t('diagnostics');
    const isDisabled = isFetching || !diagnosticsState || isCalculating;
    const tooltipContent = !diagnosticsState ? t('noAssociatedDiagnosticsReport') : t('downloadCalculationDiagnostics');

    return (
        <Tooltip content={tooltipContent.toString()} relationship='description' appearance='inverted' withArrow>
            <Button
                icon={<ClipboardPulse20Regular />}
                disabled={isDisabled}
                disabledFocusable={!diagnosticsState}
                onClick={handleDownload}
                aria-label={label}
            >
                {label}
            </Button>
        </Tooltip>
    );
};

export default CalculationDiagnosticsButton;
